/**
 * Markdown 标题提取 & 锚点生成工具
 */

/** 解析出的标题条目 */
export interface IHeadingItem {
  /** 锚点 id */
  id: string;
  /** 标题文本 */
  text: string;
  /** 标题级别 1-6 */
  level: number;
  /** 层级编号，如 "1"、"1.1"、"1.1.1" */
  number: string;
}

/**
 * 将文本转为 URL 友好锚点 id
 */
const toSlug = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[\s]+/g, '-')       // 空白 → 连字符
    .replace(/[^\w\u4e00-\u9fa5-]/g, '') // 仅保留字母、数字、中文、连字符
    .replace(/-+/g, '-')          // 合并连续连字符
    .replace(/^-|-$/g, '');       // 去掉首尾连字符

/**
 * 从 markdown 原文中提取所有标题（跳过代码块内标题）
 * @param md - markdown 原文
 * @param maxLevel - 最大标题级别，默认 6
 */
export const extractHeadings = (md: string, maxLevel = 6): IHeadingItem[] => {
  const result: IHeadingItem[] = [];
  const seen = new Map<string, number>();
  const counter = [0, 0, 0, 0, 0, 0];
  const headingRegex = new RegExp(`^(#{1,${maxLevel}})\\s+(.+)$`);

  // 逐行解析，跳过代码块内容
  const lines = md.replace(/\r/g, '').split('\n');
  let inCodeBlock = false;

  for (let i = 0;i < lines.length;i++) {
    const line = lines[i];

    // 检测代码块开始/结束（``` 或 ~~~，支持 3 个以上反引号）
    if (/^(`{3,}|~{3,})/.test(line.trim())) {
      inCodeBlock = !inCodeBlock;
      continue;
    }

    if (inCodeBlock) continue;

    const match = line.match(headingRegex);
    if (!match) continue;

    const level = match[1].length;
    const text = match[2].trim();
    let id = toSlug(text);

    // 锚点去重：同名加后缀 -2, -3...
    if (seen.has(id)) {
      seen.set(id, seen.get(id)! + 1);
      id = `${id}-${seen.get(id)}`;
    } else {
      seen.set(id, 1);
    }

    // 计算层级编号：若父级（1 ~ level-1）未初始化（为 0），设为 1，避免 "0.1"
    for (let j = 0;j < level - 1;j++) {
      if (counter[j] === 0) counter[j] = 1;
    }
    counter[level - 1] += 1;
    for (let j = level;j < 6;j++) {
      counter[j] = 0;
    }
    const number = counter.slice(0, level).join('.');

    result.push({ id, text, level, number });
  }

  return result;
};
